import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Tooltip, TooltipAlign, TooltipPosition } from '@loadsmart/loadsmart-ui'
import { Button, type ButtonSize, type ButtonVariant } from '@loadsmart/miranda-react'

import { Icon, type IconName } from 'core/ui/components/icon/Icon'

import { analytics } from 'legacy/utils/analytics'
import user from 'legacy/utils/user'

import { PreferredLaneRateModal } from '../../legacy/containers/PreferredLanes/PreferredLaneRateModal/PreferredLaneRateModal'
import { type PreferredLane } from '../domain/PreferredLane'
import { formatTooltipMessage } from '../presenters/PreferredLanePresenter'
import { usePreferredLane } from '../ui/hooks/usePreferredLane'

export type PreferredLaneToggleProps = {
  analyticsPrefix: string
  preferredLane: PreferredLane
  compact?: boolean
}

export function PreferredLaneToggle({
  analyticsPrefix,
  preferredLane,
  compact = false,
}: PreferredLaneToggleProps) {
  const {
    preferredLaneId,
    isLoading,
    savePreferredLane,
    checkIfPreferredLaneExists,
    removePreferredLane,
  } = usePreferredLane()
  const [rateModalOpen, setRateModalOpen] = useState(false)
  const disableButton = !preferredLane || isLoading

  const handleCreatePreferredLane = useCallback(async () => {
    try {
      await savePreferredLane(preferredLane)
      analytics.tag(['Preferred Lane Toggle', 'Added Preferred Lane'])
      analytics.track(`${analyticsPrefix} / Preferred Lane / Add`)
      toast.success('Preferred lane saved successfully')
      setRateModalOpen(true)
    } catch (error) {
      toast.error((error as Error).message)
    }
  }, [analyticsPrefix, preferredLane, savePreferredLane])

  const handleRemovePreferredLane = useCallback(async () => {
    try {
      await removePreferredLane()
      analytics.tag(['Preferred Lane Toggle', 'Removed Preferred Lane'])
      analytics.track(`${analyticsPrefix} / Preferred Lane / Remove`)
      toast.info('Preferred lane removed')
    } catch (error) {
      toast.error((error as Error).message)
    }
  }, [analyticsPrefix, removePreferredLane])

  const handleToggleButtonClick = useCallback(() => {
    if (!preferredLaneId) {
      handleCreatePreferredLane()
    } else {
      handleRemovePreferredLane()
    }
  }, [preferredLaneId, handleCreatePreferredLane, handleRemovePreferredLane])

  const handleClosePreferredLaneRateModal = useCallback(() => {
    setRateModalOpen(false)
  }, [])

  useEffect(() => {
    if (user.isLoggedIn()) checkIfPreferredLaneExists(preferredLane)
  }, [preferredLane, checkIfPreferredLaneExists])

  const isLaneSaved = Boolean(preferredLaneId)
  let buttonLabel: string
  let iconName: IconName
  let size: ButtonSize = 'default'
  let variant: ButtonVariant

  if (compact) {
    buttonLabel = isLaneSaved ? 'Saved preferred lane' : 'Save preferred lane'
    size = 'small'
    iconName = 'markers-favorite'
    variant = isLaneSaved ? 'primary' : 'secondary'
  } else {
    buttonLabel = isLaneSaved
      ? 'You will be notified about loads in this lane'
      : 'I want to be notified about loads in this lane'
    iconName = 'bell'
    variant = isLaneSaved ? 'secondary' : 'primary'
  }

  const { origin, destination, equipmentTypes } = formatTooltipMessage(preferredLane)

  return (
    <>
      <Tooltip
        align={TooltipAlign.Center}
        message={
          <>
            {origin} <br /> {destination} <br /> {equipmentTypes}
          </>
        }
        position={TooltipPosition.Bottom}
      >
        <Button
          type="button"
          leading={<Icon name={iconName} />}
          size={size}
          variant={variant}
          onClick={handleToggleButtonClick}
          disabled={disableButton}
        >
          {buttonLabel}
        </Button>
      </Tooltip>
      {rateModalOpen && preferredLaneId && (
        <PreferredLaneRateModal
          preferredLaneId={preferredLaneId}
          lane={preferredLane}
          onClose={handleClosePreferredLaneRateModal}
          analyticsPrefix={analyticsPrefix}
        />
      )}
    </>
  )
}
